import angular from 'angular';
import _ from 'lodash';
import { keyBy } from 'src/app/shared/utils';
import { color_items } from 'src/app/features/items/utils/item.constants';

CategoriesCtrl.$inject = ["$scope", "$translate", "user", "categories", "departments", "printers", "entityManager", "confirmDialog", "alertDialog", "checkManager", "itemSelector", "ZPayBridge"];

function CategoriesCtrl($scope, $translate, user, categories, departments, printers, entityManager, confirmDialog, alertDialog, checkManager, itemSelector, ZPayBridge) {
    const isMagoEnabled =  checkManager.isMagoEnabled();
    let variationLinkableItems = [];
    let variationLinkableItemsByUuid = {};

    const createIndexes = () => Array(20).fill().map((_, i) => i + 1);

    Object.assign($scope, {
        user: user,
        topbar_context: {
            showcaseView: 'crudviewmode',
            back_label: $translate.instant('ITEMS.CATEGORIES.PRODUCTS'),
            current_label: $translate.instant('ITEMS.CATEGORIES.CATEGORIES')
        },
        maximumIndexes: createIndexes(),
        saveInProgress: false,
        categories: _.sortBy(categories, 'index'),
        departments: departments,
        pristineCategory: null,
        kioskEnabled: checkManager.isModuleEnabled('kiosk'),
        websiteEnabled: checkManager.isModuleEnabled('c_sito_base'),
        scaleCategoriesEnabled: !!checkManager.getPreference('cashregister.scales.manage_categories'),
        itemColors: [{
            value: null,
            name: $translate.instant('ITEMS.DETAILS.COLOR_NONE')
        }, ...color_items.map(color => ({
            name: $translate.instant(color.name),
            value: color.value,
        }))],
        priceLists: [],
        printers: printers,
        zPayBridgePOS: []
    });

    $scope.getDepartmentVat = function(vat) {
        if(!vat) {
            return '';
        }

        return !vat.value && vat.code
            ? `(${$translate.instant('ITEMS.DEPARTMENTS.VAT_EXEMPTION_' + vat.code)})`
            : `(${$translate.instant('ITEMS.DETAILS.VAT', {value: vat.value})})`;
    };

    for(let idx = 1; idx <= 10; idx++) {
        if(idx === 1 || !checkManager.getPreference(`price_list_${idx}_hide`)) {
            let priceListName = checkManager.getPreference(`price_list_${idx}_name`) || `${$translate.instant('ITEMS.DETAILS.PRICE_LIST_START')} ${idx}`;

            $scope.priceLists.push({
                index: idx,
                departmentLabel: `${$translate.instant('ITEMS.DETAILS.DEPARTMENT')} ${priceListName}`,
                departmentFieldName: idx === 1 ? 'department_id' : `department${idx}_id`,
                priceFieldName: `price${idx}`,
                value: priceListName
            });
        }
    }

    $scope.canSave = function() {
        return !_.isEqual($scope.category, $scope.pristineCategory);
    };

    $scope.afterSelectCategory = function() {
        if(!$scope.pristineCategory.variations) {
            $scope.pristineCategory.variations = [];
        }

        for (const variation of $scope.pristineCategory.variations) {
            computeAvailableIndexes(variation);

            for (const variationValue of variation.variation_values) {
                if(variationValue.linked_item_uuid) {
                    variationValue._linkedItemName = variationLinkableItemsByUuid[variationValue.linked_item_uuid]?.name;
                }
            }
        }

        $scope.category = structuredClone($scope.pristineCategory);

    };

    // The first category is selected by default, can be changed to undefined and {}
    var selectDefaultCategory = function() {
        $scope.pristineCategory = _.isEmpty($scope.categories) ? {} : _.head($scope.categories);

        $scope.afterSelectCategory();
    };

    const loadVariationLinkableItems = async () => {
        const items = await entityManager.items.fetchCollectionOffline();
        variationLinkableItems = items.filter((item) => (item.uuid && !item.combinations.length));
        variationLinkableItemsByUuid = keyBy(variationLinkableItems, (i) => i.uuid);
    };

    loadVariationLinkableItems().finally(selectDefaultCategory);

    $scope.deleteCategory = async () => {
        if (!$scope.category.id) {
            return;
        }

        if(isMagoEnabled) {
            alertDialog.show($translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED'));
            return;
        }

        const answer = await confirmDialog.show($translate.instant('ITEMS.CATEGORIES.WANT_TO_DELETE'));

        if(!answer) {
            return;
        }

        try {
            const success = await entityManager.categories.deleteOneOnline($scope.category.id, true);
            $scope.categories = $scope.categories.filter((category) => category.id !== success);

            selectDefaultCategory();
        } catch (error) {
            alertDialog.show($translate.instant('ITEMS.CATEGORIES.ERROR_WHILE_DELETING'));
        }
    };

    $scope.addNewCategory = function() {
        if(isMagoEnabled) {
            alertDialog.show($translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED'));
            return;
        }

        // Fields are now blank, category is shown on cashregister/orders by default
        $scope.pristineCategory = {
            display: true,
            display_kiosk: false,
            display_website: false,
            display_slave_device: true,
            variations: []
        };

        $scope.afterSelectCategory();
    };

    $scope.addNewVariation = async () => {
        $scope.category.variations.push({
            name: "",
            required: false,
            variation_values: [],
            availableIndexes: createIndexes()
        });
    };

    $scope.copyVariationFromItem = async () => {
        const itemsWithVariations = await entityManager.items.fetchCollectionOffline()
            .then(items => items.filter(item => item.variations.length > 0));

        return $scope.copyVariation(itemsWithVariations);
    };

    $scope.copyVariationFromCategory = async () => {
        const categoriesWithVariations = await entityManager.categories.fetchCollectionOffline()
            .then(categories => categories.filter(category => category.variations.length > 0));

        return $scope.copyVariation(categoriesWithVariations);
    };

    $scope.copyVariation = async (source) => {
        const sourceToCopy = await itemSelector.show(source);
        const variations = sourceToCopy?.variations;

        if (!variations?.length) {
            return;
        }

        const variationToCopy = await itemSelector.show(variations);

        if(!variationToCopy) {
            return;
        }

        const { id, deleted_at, created_at, updated_at, ...copiedVariation } = variationToCopy;

        copiedVariation.variation_values = copiedVariation.variation_values.map(({ id, deleted_at, created_at, updated_at, ...variationValue }) => variationValue);

        for (const variationValue of copiedVariation.variation_values) {
            // set _linkedItemName
            if (variationValue.linked_item_uuid) {
                const result = variationLinkableItems.filter(item => item.uuid === variationValue.linked_item_uuid);

                if (result && result.length) {
                    variationValue._linkedItemName = result[0].name;
                }
            }
            // set _images
            if (variationValue.image_url) {
                variationValue._images = [{ image_url: variationValue.image_url }];
            }
        }

        $scope.category.variations.push({
            ...copiedVariation,
            availableIndexes: createIndexes()
        });
    };

    $scope.removeVariation = async (variationIndex) => {
        $scope.category.variations.splice(variationIndex, 1);
    };

    $scope.addVariationValue = async (variation) => {
        // Add the new variation option to the scope
        variation.variation_values.push({
            value: "",
            price_difference: 0
        });
    };

    $scope.removeVariationValue = (variationIndex, valueIndex) => {
        const variation = $scope.category.variations[variationIndex];
        const variationValue = variation.variation_values[valueIndex];

        // Put the index back into availableIndexes
        if (variationValue.index != null) {
            variation.availableIndexes.push(variationValue.index);
        }

        variation.variation_values.splice(valueIndex, 1);
    };

    $scope.onVariationValueDefaultChange = function(variation, variationValue) {
        if(variationValue.default_value) {
            _.forEach(variation.variation_values, function(vVal) {
                if(vVal !== variationValue) {
                    vVal.default_value = false;
                }
            });
        }
    };

    $scope.removeVariationLinkedItem = (variationValue) => {
        Object.assign(variationValue, {
            linked_item_uuid: null,
            _linkedItemName: null
        });
    };

    $scope.editVariationLinkedItem = async (variationValue) => {
        const selectedItem = await itemSelector.show(variationLinkableItems);

        Object.assign(variationValue, {
            linked_item_uuid: selectedItem.uuid,
            _linkedItemName: selectedItem.name,
            value: variationValue.value || selectedItem.name
        });
    };

    const computeAvailableIndexes = function(v) {
        //Find indexes between 1 and 20 that are not used by other variations and sort them
        const usedIndexes = v.variation_values.map((v) => v.index);

        v.availableIndexes = createIndexes().filter(i => !usedIndexes.includes(i)).sort((a, b) => a - b);
    };

    $scope.updateIndexes = function(variation, index, oldValue, variationValueIndex) {
        if(index == null || variation.availableIndexes.includes(index)) {
            computeAvailableIndexes(variation);
        } else {
            alertDialog.show($translate.instant('ITEMS.DETAILS.INDEX_ALREADY_USED'));
            variation.variation_values[variationValueIndex].index = parseInt(oldValue) || undefined;
        }
    };

    $scope.saveEdit = async () => {
        if(isMagoEnabled) {
            alertDialog.show($translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED'));
            return;
        }

        if($scope.saveInProgress) {
            return;
        }

        const result = $scope.categories.find((category) => category.name === $scope.category.name && category.id !== $scope.category.id);

        if (result) {
            alertDialog.show($translate.instant('ITEMS.CATEGORIES.CATEGORY_ALREAD_EXISTING'));
            return;
        }

        // Send to the BE
        const entityToBE = structuredClone($scope.category);
        $scope.saveInProgress = true;

        try {
            const functionToCall = entityToBE.id ? 'putOneOnline' : 'postOneOnline';
            const updatedEntity = await entityManager.categories[functionToCall](entityToBE);

            if(entityToBE.id) { //After PUT
                angular.copy(updatedEntity, $scope.pristineCategory);
            } else { //After POST
                $scope.categories.push(updatedEntity);
                $scope.pristineCategory = updatedEntity;
            }

            $scope.afterSelectCategory();
        } catch (error) {
            // Show error dialog
            alertDialog.show($translate.instant('ITEMS.CATEGORIES.ERROR_TRY_LATER'));
        } finally {
            $scope.saveInProgress = false;
        }
    };

    $scope.abortEdit = function() {
        if ($scope.category.id) {
            // Update the (form) view
            $scope.afterSelectCategory();
        } else {
            selectDefaultCategory();
        }
    };

    $scope.quantityMinValue = 0;

    $scope.onVariationRequiredChange = function (variation) {
        if(!variation.required && variation.min_quantity) {
            delete variation.min_quantity;
        }

        $scope.quantityMinValue = variation.required ? 1 : 0;
    };

    $scope.onVariationQuantityMinChange = function (variation) {
        variation.required = variation.min_quantity > 0;
        $scope.quantityMinValue = variation.required ? 1 : 0;
    };

    const loadZPayBridgePOS = async () => {
        const zPayBridgePayment = await entityManager.paymentMethods.fetchCollectionOffline({ payment_method_type_id:  42 }).then(pm => pm[0]);

        if(!zPayBridgePayment?.schema_name) {
            return;
        }

        try {
            const response = await ZPayBridge.getPOSList(zPayBridgePayment.schema_name);
            $scope.zPayBridgePOS = response;
        } catch (error) {
            console.error(error);
        }
    };

    loadZPayBridgePOS();
}

angular.module('items').controller('CategoriesCtrl', CategoriesCtrl);
